import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import NeilMethodSection from "@/components/NeilMethodSection";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check, Layers, LifeBuoy, Map, Rocket, Settings, TrendingUp, Users, Wrench } from "lucide-react";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics";

const LIFECYCLE = [
  { step: "Assess", icon: Map, body: "Walk the site, time the tasks, and document where labor pressure and inconsistency actually live." },
  { step: "Design", icon: Layers, body: "Match platforms, attachments, and coverage targets to your floorplan, surfaces, and shift patterns." },
  { step: "Deploy", icon: Rocket, body: "Delivery, mapping, network setup, route configuration, and safety review on your property." },
  { step: "Train", icon: Users, body: "Hands-on operator training, supervisor training, and documentation your team keeps." },
  { step: "Manage", icon: Settings, body: "Fleet dashboards, route tuning, and usage reporting across one site or many." },
  { step: "Support", icon: LifeBuoy, body: "24/7 line at (336) 203-3184, remote diagnostics, and on-site response coordination." },
  { step: "Optimize", icon: Wrench, body: "Preventive maintenance, consumable planning, and quarterly performance reviews." },
  { step: "Scale", icon: TrendingUp, body: "Repeat the validated playbook across additional floors, buildings, and regions." },
];

const INCLUSIONS = [
  "Robotics hardware allocation for the contract term",
  "Site assessment, mapping, and configuration",
  "Delivery, installation, and commissioning",
  "Operator and supervisor training",
  "Fleet management software access",
  "Preventive maintenance visits",
  "Remote diagnostics and support escalation",
  "Usage and performance reporting",
  "Consumable and accessory planning",
  "Multi-site rollout coordination",
];

const COMPARISON = [
  {
    model: "Traditional labor only",
    points: [
      "Capacity is limited by hiring and retention",
      "Repetitive tasks compete with higher-value work",
      "Consistency varies by shift and staffing level",
      "No capital outlay, but no capacity leverage either",
    ],
  },
  {
    model: "Buying equipment outright",
    points: [
      "Capital expense and depreciation on your balance sheet",
      "Deployment, mapping, and training handled internally",
      "Maintenance, parts, and downtime become your problem",
      "Upgrading means replacing owned assets",
    ],
  },
  {
    model: "Neil AI RaaS",
    highlight: true,
    points: [
      "Operating-expense structure with a defined monthly program",
      "Assessment, deployment, training, and support included per contract",
      "Preventive maintenance and fleet management handled by Neil AI",
      "Scale up, down, or across sites as operations change",
    ],
  },
];

const LANDSCAPE_FLOW = [
  "Site Assessment",
  "Property Mapping",
  "Fleet Design",
  "Deployment",
  "Training",
  "Operations",
  "Support",
  "Optimization",
  "Multi-Property Expansion",
];

const RaaS = () => (
  <>
    <SEO
      title="Robotics-as-a-Service (RaaS) Programs | Neil AI Global Solutions"
      description="Neil AI RaaS: assessment, deployment, training, fleet management, maintenance, and support in one managed robotics program for enterprise and multi-site operations."
      keywords="robotics as a service, RaaS, managed robotics program, robot fleet management, commercial robot subscription, landscape RaaS"
    />
    <Navigation />

    <main className="min-h-screen bg-background pt-32 md:pt-40 pb-20">
      <div className="max-w-7xl mx-auto px-4 space-y-16">
        {/* Hero */}
        <section className="rounded-3xl border border-[hsl(var(--line))] glass-effect p-8 md:p-14">
          <Badge className="mb-4 bg-primary/15 text-primary border border-primary/30">
            Robotics-as-a-Service
          </Badge>
          <h1 className="font-orbitron text-3xl md:text-5xl font-bold leading-tight max-w-4xl">
            Robotics-as-a-Service
          </h1>
          <p className="mt-5 max-w-3xl text-muted-foreground md:text-lg leading-relaxed">
            Robots are the easy part. Neil AI RaaS is the program around them — assessment, design,
            deployment, training, management, support, optimization, and scale — delivered as one
            accountable relationship instead of a purchase order.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Assess", "Design", "Deploy", "Train", "Manage", "Support", "Optimize", "Scale"].map((s) => (
              <span
                key={s}
                className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary"
              >
                {s}
              </span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-xl font-bold">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Build My RaaS Program <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl font-bold border-primary/40">
              <Link to="/robots">Explore the Robotics Ecosystem</Link>
            </Button>
          </div>
        </section>

        {/* Lifecycle */}
        <section>
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold mb-2">The RaaS Lifecycle</h2>
          <p className="text-muted-foreground max-w-3xl mb-8">
            Every Neil AI program follows the same disciplined path, whether it is one robot in one
            building or a fleet across a portfolio of properties.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {LIFECYCLE.map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={item.step} className="glass-effect border-[hsl(var(--line))] p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-mono text-primary">0{i + 1}</span>
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-orbitron font-bold text-lg mb-2">{item.step}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Inclusions */}
        <section className="rounded-3xl border border-[hsl(var(--line))] glass-effect p-8 md:p-12">
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold mb-3">
            What a Program Can Include
          </h2>
          <p className="text-muted-foreground max-w-3xl mb-8">
            Scope is set per contract. The elements below are the building blocks we assemble around
            your operation — your final program is documented in writing before anything is deployed.
          </p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {INCLUSIONS.map((item) => (
              <div key={item} className="flex items-start gap-3">
                <Check className="h-4 w-4 text-primary mt-1 shrink-0" />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-muted-foreground/80 border-t border-[hsl(var(--line))] pt-5">
            Program inclusions, terms, response times, and pricing vary by site, platform, and
            contract. Nothing on this page is a guarantee of specific savings or outcomes.
          </p>
        </section>

        {/* Why RaaS — CFO / COO */}
        <section id="why-raas">
          <Badge className="mb-4 bg-[hsl(43_75%_55%)]/15 text-[hsl(43_85%_65%)] border border-[hsl(43_75%_55%)]/30">
            For CFOs and COOs
          </Badge>
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold mb-2">Why RaaS?</h2>
          <p className="text-muted-foreground max-w-3xl mb-8">
            Three ways to add operational capacity. The right answer depends on your balance sheet,
            your internal bandwidth, and how fast you need to move.
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            {COMPARISON.map((col) => (
              <Card
                key={col.model}
                className={`p-6 border ${
                  col.highlight
                    ? "border-primary/50 bg-primary/[0.06]"
                    : "border-[hsl(var(--line))] glass-effect"
                }`}
              >
                <h3 className="font-orbitron font-bold text-lg mb-4">{col.model}</h3>
                <ul className="space-y-3">
                  {col.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <Check
                        className={`h-4 w-4 mt-0.5 shrink-0 ${
                          col.highlight ? "text-primary" : "text-muted-foreground/50"
                        }`}
                      />
                      {p}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground/80">
            Financial structure, accounting treatment, and tax implications should be reviewed with
            your own finance and accounting advisors.
          </p>
        </section>

        {/* Landscape RaaS */}
        <section className="rounded-3xl border border-primary/30 bg-primary/[0.05] p-8 md:p-12">
          <Badge className="mb-4 bg-primary/15 text-primary border border-primary/30">
            Grounds & Outdoor
          </Badge>
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold mb-3">
            Landscape Robotics-as-a-Service
          </h2>
          <p className="text-muted-foreground max-w-3xl mb-8">
            Autonomous mowing, turf management, and field marking managed as a program across golf
            courses, campuses, municipalities, resorts, and multi-property portfolios.
          </p>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
            {LANDSCAPE_FLOW.map((s, i) => (
              <li
                key={s}
                className="flex items-center gap-3 rounded-xl border border-[hsl(var(--line))] bg-background/40 px-4 py-3"
              >
                <span className="text-xs font-mono text-primary">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm font-semibold">{s}</span>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-xl font-bold">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Build My Landscape RaaS Program <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl font-bold border-primary/40">
              <Link to="/robots/landscape">See Landscape Robotics</Link>
            </Button>
          </div>
        </section>

        {/* Final CTA */}
        <section className="rounded-3xl border border-[hsl(var(--line))] glass-effect p-8 md:p-14 text-center">
          <h2 className="font-orbitron text-2xl md:text-4xl font-bold mb-4">
            Build My RaaS Program
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-7">
            Start with an automation assessment. We document the workflows, model the fleet, and put
            the program in writing before you commit to anything.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" className="rounded-xl font-bold">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Get an Automation Assessment
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl font-bold border-primary/40">
              <a href="tel:+13369624539">Call (336) 962-4539</a>
            </Button>
          </div>
        </section>
      </div>

      <div className="max-w-7xl mx-auto px-4 mt-16">
        <div className="px-4"><NeilMethodSection compact contextLabel="N.E.I.L.™ DISCOVERY FIRST" contextCopy="Every RaaS engagement begins with the N.E.I.L.™ Method. Discovery comes before the blueprint, the pilot, the deployment, and the management plan — so the program is engineered around your operation rather than rented off a shelf." /></div>
        <Footer />
      </div>
    </main>
      <section className="mx-auto w-full max-w-7xl px-[clamp(1rem,5vw,1.5rem)] pb-16">
        <a href="/robots/humanoid/healthcare" className="block rounded-2xl border border-[hsl(var(--line))] glass-effect p-6 hover:border-primary/50 transition-colors">
          <p className="text-xs uppercase tracking-wide text-accent">Humanoid Division</p>
          <p className="mt-2 font-orbitron text-lg font-bold">Humanoid Robotics for Healthcare &amp; Senior Care</p>
          <p className="mt-2 text-sm text-muted-foreground">Connected care technology, service humanoids, AI automation and Robotics-as-a-Service for hospitals and senior-living communities.</p>
        </a>
      </section>
  </>
);

export default RaaS;
